import {Dossiers, StateEnum} from '../../common/services';
import {rolesDar, RoleHelper} from '../../common/services/roles';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {DarDossiersService} from '../../common/services/dossiers/dossiers.service';
import {Individu, IndividuService} from '@Common/core/services';
import {map} from 'rxjs/operators';


/**
 * Helper pour la validation des sections, champs, selon étape et rôles
 */
export class DossierValidator {
  /**
   * 8 à 9 champs obligatoires
   */
  nb_fields_biatoss = 9; // BIATOSS
  nb_fields_enseignant = 8; // Enseignants
  progressComplete = 0;
  step = 9;

  disabledReason = '';

  /**
   * Calcul des rôles selon le connecté pour la partie "gestion"
   */
  rolesConnecte: rolesDar[] = [];
  individuConnected: Individu;
  hasRoleGestion = new ReplaySubject<boolean>(1);
  hasRoleGestion$ = this.hasRoleGestion.asObservable();
  rolesDarEnum = rolesDar;

  constructor(public dossierSvc: DarDossiersService, public individuSvc: IndividuService) {}


  /**
   * vérifie qu'un role existe dans un groupe des roles et que dossier existe
   * example d'utlisation affichage ou No des commentaires pour un groupe de role (ex: dans consultation page)
   * @param {rolesDar[]} authorizedRoles
   * @param {Dossiers} dossier
   * @return Observable<boolean>
   */

  checkAuthorizedRoles(authorizedRoles: rolesDar[], dossier: Dossiers): Observable<boolean> {
    const roleObservables = authorizedRoles.map(role => this.hasRole(role));
    return combineLatest(roleObservables).pipe(
      map(roleResults => {
        const hasAuthorizedRole = roleResults.some(hasRole => hasRole);
        const hasDossier = Boolean(dossier && dossier.id);
        return hasAuthorizedRole && hasDossier;
      })
    );
  }

  /**
   * Les roles autorisés à afficher les commentaires
   * @param dossier Dossier object.
   * @returns Observable<boolean>.
   */

  canShowComments(dossier: Dossiers): Observable<boolean> {
    const authorizedRoles = [
      rolesDar.masse_salariale,
      rolesDar.sbtcp,
      rolesDar.drh,
      rolesDar.demandeur,
      rolesDar.recrutement, // publication / recrutement spta
      rolesDar.recrutement_spte, // publication / recrutement spte
    ];
    return this.checkAuthorizedRoles(authorizedRoles, dossier);
  }

  /**
   * On affiche les commentaires pour les roles Recruteur
   * @param dossier Dossier object.
   * @returns Observable<boolean>.
   */

  canShowCommentsForRecruteur(dossier: Dossiers): Observable<boolean> {
    const recRoles = [
      rolesDar.recrutement_spte,
      rolesDar.recrutement
    ];
    return this.checkAuthorizedRoles(recRoles, dossier);
  }

  /**
   * les statuts du dossier pour lesquelles on affiche les commentaires
   * @param dossier Dossier object.
   * @returns Observable<boolean>.
   */

  hasValidStatut(dossier: Dossiers): Observable<boolean> {
    const validStates = [
      StateEnum.drh,
      StateEnum.commission,
      StateEnum.sbtcp,
      StateEnum.masse_salariale,
    ];
    return of(validStates.includes(dossier.state));
  }

  /**
   * On combine les Observables pour avoir un resultat final
   * qui determine si on dois ou pas afficher les commentaires
   * @param dossier Dossier object.
   * @returns Observable<boolean>.
   */

  shouldShowComments(dossier: Dossiers): Observable<boolean> {
    return this.canShowComments(dossier);
  }

  /**
   * Le connecté a-t-il le role passé en paramètre ?
   * @param {rolesDar} role : recherche des rôles connecté sur ce rôle
   * @return Observable<boolean>
   */
  hasRole(role: rolesDar): Observable<boolean> {
    return RoleHelper.hasRoleConnecteAsync(role, this.rolesConnecte);
  }

  /**
   * L'emploi n'est plus éditable si l'état > DRH
   * @param state
   */
  canEditEmploi(state: StateEnum) {
    return new Observable(observer => {
      const can = this.dossierSvc.canEditEmploi(state);
      observer.next(can);
      observer.complete();
    });
  }

  /**
   * Grisé ou non (permet d'actionner ou non) des boutons pour cette étape
   * Une étape correspond à un ou plusieurs rôles, le connecté doit au moins avoir le bon rôle pour actionner le(s) bouton(s)
   * + règles de gestion de validation du formulaire
   */
  isEtapeRoleValide(dossier) {
    this.disabledReason = '';
    let isValid = true;

    if (dossier.state !== StateEnum.demande) {
      const canManageButtons = this._haveButtonsStateRoles(dossier);
      if (!canManageButtons) {
        this._setDisabledReason('Votre rôle ne permet pas d\'actions sur ce dossier à cette étape');
        return false;
      }
    }

    if (dossier.state === StateEnum.demande) {
      return this._canSendWorkflowDemandeur(dossier);
    }

    if (dossier.state === StateEnum.masse_salariale) {
      if (dossier.avis_ms !== null && dossier.plafond_emploi !== null) {
        return true;
      } else {
        this._setDisabledReason('L\'avis masse salariale et le plafond d\'emploi sont obligatoires pour continuer vers l\'étape SBTCP');
        isValid = false;
      }
    }

    if (dossier.state === StateEnum.sbtcp) {
      if (dossier.avis_sbtcp !== null) {
        return true;
      } else {
        this._setDisabledReason('L\'avis SBTCP est obligatoire pour continuer vers l\'étape DRH');
        isValid = false;
      }
    }

    if (dossier.state === StateEnum.drh) {
      if (dossier.avis_drh !== null && (dossier.cout_estime !== null && dossier.cout_estime > 0) && (dossier.numero_emploi !== null )) {
        return true;
      } else {
        let raison = '';

        if (dossier.avis_drh === null) {
          raison = 'l\'avis DRH est obligatoire';
        }

        if (dossier.numero_emploi === null || dossier.numero_emploi === '') {
          raison = `${raison} ${raison ? '-' : ''} le numéro d'emploi est obligatoire`;
        }

        if (dossier.cout_estime === null || dossier.cout_estime <= 0) {
          raison = `${raison} ${raison ? '-' : ''} le coût estimé doit être saisi`;
        }

        raison = `Afin de passer à l'étape suivante, remplir les conditions suivantes : ${raison}`;

        this._setDisabledReason(raison);
        isValid = false;
      }
    }

    if (dossier.state === StateEnum.commission) {
      return true;
    }

    if (dossier.state === StateEnum.spta || dossier.state === StateEnum.spta_traitement) {
      return true;
    }

    if (dossier.state === StateEnum.recrutement || dossier.state === StateEnum.publication) {
      return true;
    }

    if (dossier.state === StateEnum.recrutement_spte) {
      return true;
    }

    return isValid;
  }

  isIndConnected(uid: string) {
    return uid === this.individuConnected?.uid;
  }
  /**
   * Calcul des rôles du connecté
   *
   * @private
   */
  _initHasRole() {
    this.rolesConnecte = [];

    this.individuSvc.individu$.subscribe(_individu => {
        this.individuConnected = _individu;
        this.hasRoleGestion.next(false);

        RoleHelper.setRolesConnecte(this.individuSvc, _individu, this.rolesConnecte);

        if (this.rolesConnecte.length > 0) {
          this.hasRoleGestion.next(true);
        }
      }
    );
  }

  /**
   * Vérifie que le rôle du connecté permet de manipuler les boutons selon son rôle et l'étape du dossier
   * @private
   */
  private _haveButtonsStateRoles(dossier) {
    let haveButtons = false;
    const buttonForValidRoles = this.dossierSvc.getStateForRoles();

    if (buttonForValidRoles && buttonForValidRoles.has(dossier.state)) {
      const roles = buttonForValidRoles.get(dossier.state);

      roles.forEach((_role) => {
        this.hasRole(_role).subscribe((result) => {
          if (result) {
            haveButtons = true;
          }
        });
      });
    }

    return haveButtons;
  }

  /**
   * Le demandeur peut-il envoyer sa demande à la Dipefas ?
   */
  private _canSendWorkflowDemandeur(dossier) {
    if (dossier.type_demande === null) {
      this._setDisabledReason('La cible de recrutement doit être rempli');
      return false;
    }

    if (this.progressComplete < 100) {
      // tslint:disable-next-line:max-line-length
      this._setDisabledReason('Le dossier est incomplet pour l\'envoyer à la Dipefas. \nVeuillez remplir tous les champs obligatoires marqués d\'un *');
      return false;
    }

    return true;
  }

  private _setDisabledReason(message: string = '') {
    this.disabledReason = message;
  }

}
